import React, { useState, useEffect } from 'react';
import { Input, Label, Textarea, Button, Toggle } from '../../ui';
import { AlertCircle, Loader2, Sparkles } from 'lucide-react';
import { getHelpCenter, updateHelpCenter, updateHelpCenterSettings } from '../../../services/api';
import type { HelpCenter, IpAllowlistEntry, UpdateHelpCenterRequest } from '../../../types';
import toast from 'react-hot-toast';
import { HelpCenterBranding } from './HelpCenterBranding';
import { HelpCenterPasswordSettings } from './HelpCenterPasswordSettings';
import { HelpCenterLinksSettings } from './HelpCenterLinksSettings';
import { HelpCenterCustomDomain } from './HelpCenterCustomDomain';
import { IpAllowlistSettings } from '../access/IpAllowlistSettings';
import { useIsProCloudPlan } from '../../../store/userStore';
import { useUIStore } from '../../../store/uiStore';
import { Badge } from '../../ui/badge';
import UpsellModal from '../../modals/UpsellModal';
import { useTranslation } from 'react-i18next';

interface HelpCenterSettingsProps {
  helpCenterId: number | string;
  onSuccess?: () => void;
  onClose?: () => void;
}

export const HelpCenterSettings: React.FC<HelpCenterSettingsProps> = ({
  helpCenterId,
  onClose,
}) => {
  const [helpCenter, setHelpCenter] = useState<HelpCenter | null>(null);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [activeTab, setActiveTab] = useState<'settings' | 'branding' | 'security' | 'links' | 'seo' | 'domain'>('settings');
  const isProCloudPlan = useIsProCloudPlan();
  const { openModal, modals, closeModal } = useUIStore();
  const { t } = useTranslation();

  const [form, setForm] = useState({
    title: '',
    description: '',
    slug: '',
  });
  const [isIndexable, setIsIndexable] = useState(false);
  const [feedbackEnabled, setFeedbackEnabled] = useState(false);
  const [sidebarTocEnabled, setSidebarTocEnabled] = useState(false);
  const [robotsTxt, setRobotsTxt] = useState('');
  const [isSavingRobotsTxt, setIsSavingRobotsTxt] = useState(false);
  const MAX_ROBOTS_TXT_LENGTH = 16 * 1024; // 16 KB

  // Fetch help center data when component mounts
  useEffect(() => {
    if (helpCenterId) {
      fetchHelpCenter();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [helpCenterId]);

  // Reset form when help center changes
  useEffect(() => {
    if (helpCenter) {
      setForm({
        title: helpCenter.title || '',
        description: helpCenter.description || '',
        slug: helpCenter.slug || '',
      });
      setSlugManuallyEdited(true);
      const indexableValue = Boolean(helpCenter.settings?.isIndexable);
      const feedbackEnabledValue = Boolean(helpCenter.settings?.feedbackEnabled);
      const sidebarTocValue = helpCenter.settings?.tocPlacement === 'sidebar';
      setIsIndexable(indexableValue);
      setFeedbackEnabled(feedbackEnabledValue);
      setSidebarTocEnabled(sidebarTocValue);
      setRobotsTxt(typeof helpCenter.settings?.robotsTxt === 'string' ? helpCenter.settings.robotsTxt : '');
    }
  }, [helpCenter]);

  const handleIndexableChange = (checked: boolean) => {
    setIsIndexable(checked);
  };

  const fetchHelpCenter = async () => {
    try {
      setLoading(true);
      setFormError(null);
      const response = await getHelpCenter(helpCenterId.toString());
      
      if (response.ok && response.helpCenter) {
        setHelpCenter(response.helpCenter);
      } else {
        const errorMessage = response.errors && response.errors.length > 0 
          ? response.errors[0]
          : response.error || t('helpCenter.settings.fetchFailed');
        setFormError(errorMessage);
      }
    } catch (err) {
      console.error('Error fetching help center:', err);
      setFormError(t('helpCenter.settings.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (title: string) => {
    setForm({
      ...form,
      title,
      // Only auto-generate slug if user hasn't manually edited it
      slug: slugManuallyEdited ? form.slug : generateSlug(title),
    });
    // Clear error when user starts typing
    if (formError) {
      setFormError(null);
    }
  };

  const handleSlugChange = (slug: string) => {
    setForm({ ...form, slug });
    // Mark slug as manually edited when user types in it
    setSlugManuallyEdited(true);
    // Clear error when user starts typing
    if (formError) {
      setFormError(null);
    }
  };

  const handleSettingsSubmit = async () => {
    setIsSubmitting(true);
    setFormError(null);

    try {
      const trimmedSlug = form.slug.trim();
      const originalSlug = helpCenter?.slug || '';
      
      // Only include slug in update if it was explicitly changed
      const updateData: UpdateHelpCenterRequest = {
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        // Only send slug if it's different from the original
        ...(trimmedSlug !== originalSlug && trimmedSlug ? { slug: trimmedSlug } : {}),
      };

      const response = await updateHelpCenter(helpCenterId.toString(), updateData);
      
      if (response.ok && response.helpCenter) {
        setHelpCenter(response.helpCenter);
        toast.success(t('helpCenter.settings.updateSuccess'), {
          position: 'top-center',
          duration: 3000,
        });
        // Don't call onSuccess for settings updates - it triggers full refresh and closes drawer
        // Only update local state to keep drawer open (similar to branding updates)
        // If slug changed, user will need to refresh manually or navigate
      } else {
        const errorMessage = response.errors && response.errors.length > 0 
          ? response.errors[0]
          : response.error || t('helpCenter.settings.updateFailed');
        setFormError(errorMessage);
      }
    } catch (err) {
      console.error('Error updating help center:', err);
      setFormError(t('helpCenter.settings.updateFailedRetry'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordUpdate = async (settings: {
    password?: string;
    passwordEnabled?: boolean;
    passwordValidDays?: number;
  }) => {
    try {
      const response = await updateHelpCenterSettings(
        helpCenterId.toString(),
        settings,
        undefined,
        false
      );
      
      if (response.ok && response.helpCenter) {
        setHelpCenter(response.helpCenter);
      } else {
        const errorMessage = response.errors && response.errors.length > 0 
          ? response.errors[0]
          : response.error || t('helpCenter.settings.passwordUpdateFailed');
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Error updating password settings:', err);
      throw err;
    }
  };

  const handleIpAllowlistSave = async (params: { enabled: boolean; entries: IpAllowlistEntry[] }) => {
    const response = await updateHelpCenterSettings(
      helpCenterId.toString(),
      { ipAllowlistEnabled: params.enabled, ipAllowlist: params.entries },
      undefined,
      false
    );
    if (response.ok && response.helpCenter) {
      setHelpCenter(response.helpCenter);
    }
    return {
      ok: response.ok,
      errors: response.errors,
      entryErrors: response.entryErrors,
      entries: response.helpCenter?.settings?.ipAllowlist,
    };
  };

  const handleBrandingUpdate = async (
    settings: {
      themeColor?: string;
      logoUrl?: string;
      companyUrl?: string;
    },
    logoFile?: File | null,
    removeFile?: boolean
  ) => {
    try {
      const response = await updateHelpCenterSettings(
        helpCenterId.toString(), 
        settings,
        logoFile,
        removeFile
      );
      
      if (response.ok && response.helpCenter) {
        setHelpCenter(response.helpCenter);
        toast.success(t('helpCenter.settings.brandingUpdateSuccess'), {
          position: 'top-center',
          duration: 2000,
        });
        // Don't call onSuccess for branding updates - it triggers full refresh
        // Only update local state to keep drawer open
      } else {
        const errorMessage = response.errors && response.errors.length > 0 
          ? response.errors[0]
          : response.error || t('helpCenter.settings.brandingUpdateFailed');
        toast.error(errorMessage, {
          position: 'top-center',
          duration: 3000,
        });
      }
    } catch (err) {
      console.error('Error updating branding:', err);
      toast.error(t('helpCenter.settings.brandingUpdateFailedRetry'), {
        position: 'top-center',
        duration: 3000,
      });
      throw err;
    }
  };

  const handleLinksUpdate = async (headerLinks: any[], footerLinks: any[]) => {
    try {
      const response = await updateHelpCenterSettings(
        helpCenterId.toString(),
        {
          headerLinks,
          footerLinks,
        },
        undefined,
        false
      );
      
      if (response.ok && response.helpCenter) {
        setHelpCenter(response.helpCenter);
      } else {
        const errorMessage = response.errors && response.errors.length > 0 
          ? response.errors[0]
          : response.error || t('helpCenter.settings.linksUpdateFailed');
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Error updating links:', err);
      toast.error(t('helpCenter.settings.linksUpdateFailed'), {
        position: 'top-center',
        duration: 3000,
      });
      throw err;
    }
  };

  const handleSeoSave = async () => {
    if (robotsTxt.length > MAX_ROBOTS_TXT_LENGTH) {
      toast.error(t('helpCenter.settings.robotsTxtTooLong', { max: MAX_ROBOTS_TXT_LENGTH }), {
        position: 'top-center',
        duration: 3000,
      });
      return;
    }
    setIsSavingRobotsTxt(true);
    try {
      const payload = {
        isIndexable,
        ...(isProCloudPlan ? { robotsTxt: robotsTxt.trim() } : {}),
      };
      const response = await updateHelpCenterSettings(
        helpCenterId.toString(),
        payload,
        undefined,
        false
      );
      if (response.ok && response.helpCenter) {
        setHelpCenter(response.helpCenter);
        setRobotsTxt(response.helpCenter.settings?.robotsTxt ?? '');
        setIsIndexable(response.helpCenter.settings?.isIndexable ?? false);
        toast.success(t('helpCenter.settings.seoSaveSuccess'), {
          position: 'top-center',
          duration: 3000,
        });
      } else {
        const errorMessage = response.errors?.length
          ? response.errors[0]
          : response.error || t('helpCenter.settings.seoSaveFailed');
        toast.error(errorMessage, { position: 'top-center', duration: 3000 });
      }
    } catch (err) {
      console.error('Error saving SEO settings:', err);
      toast.error(t('helpCenter.settings.seoSaveFailedRetry'), {
        position: 'top-center',
        duration: 3000,
      });
    } finally {
      setIsSavingRobotsTxt(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!helpCenter) {
    return (
      <div className="p-4">
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
          <p className="text-sm text-destructive">
            {formError || t('helpCenter.settings.notFound')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Tabs */}
      <div className="flex border-b mb-4">
        <button
          onClick={() => setActiveTab('settings')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'settings'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {t('common.settings')}
        </button>
        <button
          onClick={() => setActiveTab('branding')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'branding'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {t('helpCenter.settings.tabBranding')}
        </button>
        <button
          onClick={() => setActiveTab('security')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'security'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {t('helpCenter.settings.tabSecurity')}
        </button>
        <button
          onClick={() => setActiveTab('links')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'links'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {t('helpCenter.settings.tabLinks')}
        </button>
        <button
          onClick={() => setActiveTab('seo')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'seo'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {t('helpCenter.settings.tabSeo')}
        </button>
        <button
          onClick={() => setActiveTab('domain')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'domain'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {t('helpCenter.settings.tabDomain')}
        </button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'settings' && (
          <div className="space-y-4">
            {/* Error Display */}
            {formError && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                <p className="text-sm text-destructive">{formError}</p>
              </div>
            )}

            <div>
              <Label htmlFor="title">{t('helpCenter.settings.titleLabel')}</Label>
              <Input
                id="title"
                value={form.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder={t('helpCenter.settings.titlePlaceholder')}
                className={`mt-1 ${formError ? 'border-destructive' : ''}`}
                disabled={isSubmitting}
              />
            </div>
            <div>
              <Label htmlFor="slug">{t('helpCenter.settings.slugLabel')}</Label>
              <Input
                id="slug"
                value={form.slug}
                onChange={(e) => handleSlugChange(e.target.value)}
                placeholder={t('helpCenter.settings.slugPlaceholder')}
                className={`mt-1 ${formError && formError.includes('Slug') ? 'border-destructive' : ''}`}
                disabled={isSubmitting}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('helpCenter.settings.slugDescription')}
              </p>
            </div>
            <div>
              <Label htmlFor="description">{t('helpCenter.settings.descriptionLabel')}</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={t('helpCenter.settings.descriptionPlaceholder')}
                className="mt-1"
                rows={3}
                disabled={isSubmitting}
              />
            </div>

            {/* Sidebar Table of Contents */}
            <div className="md:col-span-5">
              <div className="space-y-2 flex flex-row items-center justify-between mt-5">
                <div className="space-y-0.5">
                  <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    {t('helpCenter.sidebarToc.label')}
                  </label>
                  <p className="text-[0.8rem] text-muted-foreground">
                    {t('helpCenter.sidebarToc.description')}
                  </p>
                </div>
                <div className="ml-10 flex justify-between items-center">
                  <Toggle
                    checked={sidebarTocEnabled}
                    onChange={(checked) => {
                      setSidebarTocEnabled(checked);
                      updateHelpCenterSettings(
                        helpCenterId.toString(),
                        { tocPlacement: checked ? 'sidebar' : 'right' },
                        undefined,
                        false
                      ).then((response) => {
                        if (response.ok && response.helpCenter) {
                          setHelpCenter(response.helpCenter);
                          toast.success(t('helpCenter.sidebarToc.updated'), {
                            position: 'top-center',
                            duration: 2000,
                          });
                        }
                      }).catch((err) => {
                        console.error('Error updating navigation settings:', err);
                        toast.error(t('helpCenter.sidebarToc.updateFailed'), {
                          position: 'top-center',
                          duration: 3000,
                        });
                        setSidebarTocEnabled(!checked);
                      });
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Feedback Collection */}
            <div className="md:col-span-5">
              <div className="space-y-2 flex flex-row items-center justify-between mt-5">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      {t('helpCenter.settings.feedbackLabel')}
                    </label>
                    {!isProCloudPlan && (
                      <Badge
                        variant="outline"
                        className="cursor-pointer border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50 text-amber-700 hover:from-amber-100 hover:to-yellow-100 dark:border-amber-700 dark:from-amber-950/20 dark:to-yellow-950/20 dark:text-amber-300 dark:hover:from-amber-950/30 dark:hover:to-yellow-950/30"
                        onClick={() => openModal('upsell')}
                      >
                        <Sparkles className="h-3 w-3 mr-1" strokeWidth={2.5} />
                        {t('common.premium')}
                      </Badge>
                    )}
                  </div>
                  <p className="text-[0.8rem] text-muted-foreground">
                    {isProCloudPlan
                      ? t('helpCenter.settings.feedbackDescriptionPro')
                      : t('helpCenter.settings.feedbackDescriptionFree')}
                  </p>
                </div>
                <div className="ml-10 flex justify-between items-center">
                  <Toggle
                    checked={feedbackEnabled}
                    onChange={(checked) => {
                      if (!isProCloudPlan && checked) {
                        toast.error(t('helpCenter.settings.feedbackPaidError'), {
                          position: 'top-center',
                          duration: 3000,
                        });
                        return;
                      }
                      setFeedbackEnabled(checked);
                      // Sync immediately (no debounce needed for toggle)
                      updateHelpCenterSettings(
                        helpCenterId.toString(),
                        { feedbackEnabled: checked },
                        undefined,
                        false
                      ).then((response) => {
                        if (response.ok && response.helpCenter) {
                          setHelpCenter(response.helpCenter);
                          toast.success(t('helpCenter.settings.feedbackUpdated'), {
                            position: 'top-center',
                            duration: 2000,
                          });
                        }
                      }).catch((err) => {
                        console.error('Error updating feedback settings:', err);
                        toast.error(t('helpCenter.settings.feedbackUpdateFailed'), {
                          position: 'top-center',
                          duration: 3000,
                        });
                        // Revert on error
                        setFeedbackEnabled(!checked);
                      });
                    }}
                    disabled={!isProCloudPlan}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSettingsSubmit}
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                {t('helpCenter.settings.saveChanges')}
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'branding' && (
          <div>
            <HelpCenterBranding
              settings={helpCenter.settings}
              isProCloudPlan={isProCloudPlan}
              onUpdate={handleBrandingUpdate}
            />
          </div>
        )}

        {activeTab === 'security' && (
          <div>
            <HelpCenterPasswordSettings
              settings={helpCenter.settings}
              isProCloudPlan={isProCloudPlan}
              onUpdate={handlePasswordUpdate}
            />
            <div className="mt-8 pt-6 border-t">
              <IpAllowlistSettings
                enabled={!!helpCenter.settings?.ipAllowlistEnabled}
                entries={helpCenter.settings?.ipAllowlist || []}
                isProCloudPlan={isProCloudPlan}
                scope="help-center"
                onSave={handleIpAllowlistSave}
              />
            </div>
          </div>
        )}

        {activeTab === 'links' && (
          <div>
            <HelpCenterLinksSettings
              headerLinks={helpCenter.settings?.headerLinks || []}
              footerLinks={helpCenter.settings?.footerLinks || []}
              isProCloudPlan={isProCloudPlan}
              onUpdate={handleLinksUpdate}
            />
          </div>
        )}

        {activeTab === 'seo' && (
          <div className="space-y-4">
            <p className="text-[0.8rem] text-muted-foreground">
              {t('helpCenter.settings.seoIntro')}
            </p>
            {/* Search indexing - available to all */}
            <div className="space-y-2 flex flex-row items-center justify-between">
              <div className="space-y-0.5">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {t('helpCenter.settings.searchIndexingLabel')}
                </label>
                <p className="text-[0.8rem] text-muted-foreground">
                  {t('helpCenter.settings.searchIndexingDescription')}
                </p>
              </div>
              <div className="ml-10 flex justify-between items-center">
                <Toggle
                  checked={isIndexable}
                  onChange={handleIndexableChange}
                />
              </div>
            </div>

            {/* Custom robots.txt - paid */}
            <div className={!isProCloudPlan ? 'pointer-events-none opacity-80' : ''}>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium leading-none">{t('helpCenter.settings.customRobotsLabel')}</label>
                {!isProCloudPlan && (
                  <Badge
                    variant="outline"
                    className="cursor-pointer border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50 text-amber-700 hover:from-amber-100 hover:to-yellow-100 dark:border-amber-700 dark:from-amber-950/20 dark:to-yellow-950/20 dark:text-amber-300 dark:hover:from-amber-950/30 dark:hover:to-yellow-950/30"
                    onClick={() => !isProCloudPlan && openModal('upsell')}
                  >
                    <Sparkles className="h-3 w-3 mr-1" strokeWidth={2.5} />
                    {t('common.premium')}
                  </Badge>
                )}
              </div>
              <p className="text-[0.8rem] text-muted-foreground mt-1">
                {isProCloudPlan
                  ? t('helpCenter.settings.customRobotsDescriptionPro')
                  : t('helpCenter.settings.customRobotsDescriptionFree')}
              </p>
              <div className="relative mt-2">
                <Textarea
                  value={robotsTxt}
                  onChange={(e) => setRobotsTxt(e.target.value)}
                  placeholder={'User-agent: *\nAllow: /'}
                  className="font-mono text-sm min-h-[200px] resize-y"
                  disabled={!isProCloudPlan}
                  maxLength={MAX_ROBOTS_TXT_LENGTH}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t('helpCenter.settings.charactersCount', { current: robotsTxt.length, max: MAX_ROBOTS_TXT_LENGTH })}
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isSavingRobotsTxt}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSeoSave}
                disabled={isSavingRobotsTxt}
                loading={isSavingRobotsTxt}
              >
                {t('helpCenter.settings.saveChanges')}
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'domain' && (
          <div>
            <HelpCenterCustomDomain
              helpCenterId={helpCenterId.toString()}
              isProCloudPlan={isProCloudPlan}
              currentDomain={helpCenter.settings?.customDomain as string | undefined}
            />
          </div>
        )}
      </div>

      {/* Upsell Modal */}
      <UpsellModal
        isOpen={modals.upsell}
        onClose={() => closeModal('upsell')}
      />
    </div>
  );
};

